import { useMutation, useQuery } from '@tanstack/react-query'
import { httpClient } from '@/api/http-client'
import { MutationOptions, queryClient } from '@/api/react-query'
import { customerApplicationsQueryKey } from '@/api/financial/customer/list-customer-applications'
import {
  CashflowItem,
  applicationQueryKey,
} from '@/api/financial/application/read-application'

export type InternshipPreview = {
  summary: {
    requested: number
    financed: number
    monthly_rate: number
    phases: {
      installments: number
      value: number
      grace: number
      first_due: string
    }[]
  }
  cashflow: CashflowItem[]
  limits: {
    min_tuition: number
    max_tuition: number
    max_college_debt: number
    min_installment: number
    max_installment: number
    monthly_rate: number
    current_semester: number
    start_semesters: number[]
  }
  plan: {
    start_semester: number
    months: number
    after_installments: number
    unpaid_tuitions: number
    college_debt: number
  }
  viable: boolean
}

export type InternshipInput = {
  monthly_tuition: number
  graduation_date: string
  start_semester: number
  paid_tuitions: number
  college_debt: number
}

export function useInternshipPreview(input: InternshipInput | null) {
  return useQuery({
    queryKey: ['internship-preview', input],
    queryFn: async () => {
      const response = await httpClient('financial').post<InternshipPreview>(
        '/v1/simulation/internship',
        input,
      )
      return response.data
    },
    enabled: Boolean(
      input && input.monthly_tuition > 0 && input.graduation_date,
    ),
    retry: false,
  })
}

export function useSaveInternshipSimulation(
  applicationKey: string,
  options?: MutationOptions<InternshipPreview, InternshipInput>,
) {
  return useMutation({
    mutationFn: async (body: InternshipInput) => {
      const response = await httpClient('financial').post<InternshipPreview>(
        `/v1/application/${applicationKey}/simulation`,
        body,
      )
      return response.data
    },
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: applicationQueryKey(applicationKey),
      })
      queryClient.invalidateQueries({ queryKey: customerApplicationsQueryKey })
      options?.onSuccess?.(...args)
    },
  })
}
